import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import CopilotPanel from "@/components/CopilotPanel";
import { Sparkles, Maximize2 } from "lucide-react";

/**
 * Global floating Copilot button + slide-over drawer.
 * Hidden on the dedicated `/copilot` page to avoid duplication.
 *
 * Keyboard shortcut: Cmd/Ctrl + J
 */
export default function CopilotFab() {
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onKey = (e) => {
            if ((e.key === "j" || e.key === "J") && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // Auto-close when navigating to the dedicated page
    useEffect(() => {
        if (loc.pathname === "/copilot") setOpen(false);
    }, [loc.pathname]);

    if (loc.pathname === "/copilot") return null;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                aria-label="Open Finance Copilot"
                data-testid="copilot-fab"
                className="copilot-fab"
            >
                <Sparkles size={18} strokeWidth={1.8} />
                <span className="copilot-fab__label">Copilot</span>
                <span className="copilot-fab__kbd">⌘J</span>
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="p-0 w-full sm:max-w-md md:max-w-lg gap-0 bg-white"
                    data-testid="copilot-sheet"
                >
                    <SheetTitle className="sr-only">Finance Copilot</SheetTitle>
                    <SheetDescription className="sr-only">
                        Ask questions about your cards, expenses, statements, dues, and payments.
                    </SheetDescription>
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-end px-3 pt-3">
                            <Link
                                to="/copilot"
                                onClick={() => setOpen(false)}
                                className="topbar-btn"
                                data-testid="copilot-expand-link"
                                title="Open full page"
                            >
                                <Maximize2 size={13} /> Full page
                            </Link>
                        </div>
                        <div className="flex-1 min-h-0">
                            <CopilotPanel compact />
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
